import { Control, Controller } from 'react-hook-form';
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  FormControlLabel,
  styled,
  CircularProgress,
} from '@mui/material';
import { User } from '../../../data/models/user.model';
import { UserRole } from '../../../data/models/user-role.model';
import { useTranslation } from 'react-i18next';
import TextInput from '../../form/TextInput';

const PREFIX = 'Security';

const classes = {
  container: `${PREFIX}-container`,
  group: `${PREFIX}-group`,
  role: `${PREFIX}-role`,
  divider: `${PREFIX}-divider`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.group}`]: {
    fontWeight: 'bold',
  },

  [`& .${classes.role}`]: {
    marginLeft: theme.spacing(3),
  },

  [`& .${classes.divider}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

interface SecurityInfoFormProps {
  readonly user?: User | null;
  readonly control: Control;
  readonly userRoles: UserRole[];
  readonly loadingUserRoles: boolean;
  readonly readOnly: boolean;
}

export default function SecurityInfoForm({
  user,
  control,
  userRoles,
  loadingUserRoles,
  readOnly,
}: SecurityInfoFormProps) {
  const { t } = useTranslation('common');

  const isRoleChecked = (
    user: User | undefined | null,
    roleId: number | undefined,
  ): boolean => {
    const roles = [
      ...(user?.userCategoryInternals || []),
      ...(user?.userCategoryServiceAccounts || []),
      ...(user?.userCategoryTransportationProviders || []),
    ];
    const idx = roles.findIndex((r) => r.userRoleID === roleId);
    return idx >= 0;
  };

  const isRoleDefault = (role: UserRole): boolean => {
    return role?.isDefault ? true : false;
  };

  return (
    <StyledBox className={classes.container}>
      <FormControl>
        <FormLabel color="info">{t('users.groups')}</FormLabel>
        {loadingUserRoles && <CircularProgress />}
        {!loadingUserRoles &&
          userRoles?.map((r) => {
            const checkboxName = `UserRoleID_${r.userRoleID}`;
            const checked =
              isRoleChecked(user, r.userRoleID) || isRoleDefault(r);
            return (
              <Controller
                key={r.userRoleID}
                control={control}
                name={checkboxName}
                defaultValue={checked}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      label={
                        r.description
                          ? `${r.userRoleName} (${r.description})`
                          : r.userRoleName
                      }
                      control={
                        <Checkbox
                          color="primary"
                          onChange={(e) => field.onChange(e.target.checked)}
                          defaultChecked={checked}
                          disabled={readOnly}
                        />
                      }
                    />
                  );
                }}
              />
            );
          })}
      </FormControl>
    </StyledBox>
  );
}
